/**
 * @file StrategyDetail.jsx
 * @description Main application interface with role-specific features
 * 
 * @section Responsibilities
 * - Manage role-based views
 * - Handle data aggregation
 * - Control real-time updates
 * - Manage user preferences
 * 
 * @section Dependencies
 * - Role Management: Access control
 * - Data Services: Information handling
 * - State Management: Global state
 * 
 * @section Integration Points
 * - Data API: Information retrieval
 * - Role System: Access control
 * - Notification System: Real-time updates
 * 
 * @section State Management
 * - dashboardData: Aggregated information
 * - loadingState: Data fetching
 * - errorState: Error handling
 * 
 * @section Events
 * - onDataUpdate: Data changes
 * - onError: Error handling
 * - onPreferenceChange: Settings updates
 * 
 * @section Props
 * @prop {String} userRole - Current user role
 * @prop {Object} permissions - User permissions
 */

import React, { useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import SkinPage from "../../SkinPage";
import Widget from "../../widgets/Widget";
import Request from "../../../../utils/fetchUtils";
import TradingDash from "../../widgets/trading/TradingDash";
import OrdersHistory from "../../widgets/trading/OrdersHistory";
import Positions from "../../widgets/trading/Positions";
import PendingRequests from "../../widgets/trading/PendingRequests";
import {
  LineChart,
  Line,
  Tooltip,
  ReferenceLine,
  YAxis,
  XAxis,
} from "recharts";
import { useEffect } from "react";
import DgButton from "../../../../components/DgButton";
import Loader from "../../widgets/Loader";
import { TODAY, toISOString } from "../../../../utils/dateUtils";
import Swal from "sweetalert2";
import FilterSelect from "../../../../components/dashboard/FilterSelect";
import { getFromSession } from "../../../../utils/sessionUtils";
import WarningIcon from "@mui/icons-material/Warning";

function StrategyDetail() {
  const { id } = useParams();
  const sessionRole = getFromSession("role");
  const isCustomer = sessionRole === "customer";
  const [strategy, setStrategy] = useState({});
  const [historical, setHistorical] = useState({});
  const [orders, setOrders] = useState([]);
  const [closingPosition, setClosingPosition] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleFetchData = async (queryFilters = "") => {
    const sess = JSON.parse(localStorage.session);
    const userId = sess?.userId;

    if (typeof userId === "string") {
      setLoading(true);
      const res = await Request(`/trading/strategy/${id}?q=&${queryFilters}`);

      if (res.ok) {
        setLoading(false);
        setStrategy(res.data);
        setHistorical(res.data?.historical);
      }

      const resOrders = await Request(`/trading/strategy/${id}/orders`);
      if (resOrders.ok) {
        setOrders(resOrders.data);
      }
    }
  };

  const fetch = useCallback(() => {
    handleFetchData();
  }, []);

  const mapHistoricalToLineChart = (strategy) => {
    const data =
      strategy?.historical?.map(
        ({
          SK,
          balance: {
            balances: { total_equity },
          },
        }) => {
          const [partition, id, date] = SK.split("#");
          return {
            name: date,
            amount: total_equity,
          };
        }
      ) || [];

    const minEquity = Math.min(
      ...data?.map((d) => d.balance?.balances?.market_value)
    );
    const maxEquity = Math.max(
      ...data?.map((d) => d.balance?.balances?.market_value)
    );

    return { data, minEquity, maxEquity }; //
  };

  const getStrategyRiskScore = async () => {
    setLoading(true);
    const res = await Request(`/trading/risk/strategy/${id}`);
    setLoading(false);
    if (res.ok) {
      setStrategy({
        ...strategy,
        riskScore: res.data.riskAnalysis.risk_number,
        lastRiskCalculated: toISOString(TODAY),
      });
      Swal.fire("Risk calculated", "", "success");
    }
  };

  const handleClosingPosition = (pos) => {
    setClosingPosition(pos);
  };

  useEffect(() => {
    fetch();
  }, []);

  return (
    <SkinPage>
      <Widget name={"strategy-detail"}>
        <div className="heading">
          <h1 className="title">{strategy.name}</h1>
          {isCustomer && (
            <>
              <p style={{ color: "#95b2c5" }}>
                <WarningIcon />
                {"  "}The strategies and views presented are solely those of the
                fund manager and do not necessarily reflect the positions of
                your personal portfolio client. All investment strategies and
                approaches are implemented independently by the fund manager and
                are based on their professional judgment and expertise. For
                further clarification, please consult with your advisor or refer
                to the official documentation provided
              </p>
            </>
          )}
        </div>
        <div className="data-info">
          <div className="features">
            <div className="main-feature">
              Total clients: {strategy.customers?.length || 0}
            </div>
            <div className="main-feature">
              Orders replicated: {strategy.orders?.length || 0}
            </div>
            <div className="main-feature">
              <span>Risk score:</span>
              <br />
              <span
                style={{ textAlign: "center", display: "block", width: "100%" }}
              >
                {strategy.riskScore || "-"}
              </span>
            </div>
          </div>

          <FilterSelect onFilter={handleFetchData} />

          <div className="performance-chart">
            {(() => {
              const { data, minEquity, maxEquity } =
                mapHistoricalToLineChart(strategy);
              return (
                <LineChart width={800} height={200} data={data}>
                  <Line
                    type="monotone"
                    dataKey="amount"
                    stroke="#3357FF"
                    strokeWidth={2}
                  />
                  <Tooltip />
                  <XAxis dataKey="name" height={60} />
                  <YAxis type="number" domain={[minEquity, maxEquity]} />
                  <ReferenceLine
                    y={100000}
                    stroke="white"
                    strokeDasharray="3 3"
                  />
                </LineChart>
              );
            })()}
          </div>
          {!isCustomer && (
            <DgButton
              category={"primary"}
              size={"medium"}
              text={"Calculate risk"}
              action={() => {
                getStrategyRiskScore();
              }}
            />
          )}
        </div>
      </Widget>
      {!isCustomer && <PendingRequests strategy={strategy} fetch={fetch} />}
      {!isCustomer && (
        <TradingDash
          strategy={strategy}
          closingPosition={closingPosition}
          handleFetchData={handleFetchData}
        />
      )}
      <OrdersHistory orders={orders} strategyId={id} />
      <Positions
        strategyId={id}
        getClosingPosition={handleClosingPosition}
        positions={strategy.performance?.positions}
      />
      {loading && <Loader />}
    </SkinPage>
  );
}

export default StrategyDetail;
